import { useTranslation } from 'react-i18next'

import type { PestTone } from '@ps/common-features/helpers/pestCoverage'
import { EyeReview } from '@ps/ds/icons/EyeReview'
import { Check, type LucideIcon, X } from 'lucide-react'
import { NS } from 'src/constants/i18n'
import { TreatmentStatus } from 'src/features/treatment/constants/treatment'

type TreatmentStatusConfig = {
  icon: LucideIcon
  backgroundColor: string
  iconSize: number
}

const TREATMENT_STATUS_INDICATOR_CONFIG = {
  success: { icon: Check, backgroundColor: 'bg-emerald-500', iconSize: 8 },
  warning: { icon: EyeReview, backgroundColor: 'bg-yellow-600', iconSize: 12 },
  failure: { icon: X, backgroundColor: 'bg-neutral-400', iconSize: 8 },
} satisfies Record<string, TreatmentStatusConfig>

/**
 * Styling comes from the tone on the shared mapping row, so a status cannot be reworded
 * in one portal and restyled in the other without the row itself changing.
 */
const TONE_CONFIG = {
  covered: TREATMENT_STATUS_INDICATOR_CONFIG.success,
  excluded: TREATMENT_STATUS_INDICATOR_CONFIG.failure,
  paid: TREATMENT_STATUS_INDICATOR_CONFIG.success,
  pending: TREATMENT_STATUS_INDICATOR_CONFIG.warning,
  warranty: TREATMENT_STATUS_INDICATOR_CONFIG.warning,
} satisfies Record<PestTone, TreatmentStatusConfig>

type TreatmentStatusIndicatorProps = {
  status: TreatmentStatus
  tone: PestTone | null
}

export const TreatmentStatusIndicator = ({ status, tone }: TreatmentStatusIndicatorProps) => {
  const { t } = useTranslation(NS.TRACKING)

  if (status === TreatmentStatus.Hidden || tone === null) {
    return null
  }

  const { icon: Icon, backgroundColor, iconSize } = TONE_CONFIG[tone]

  return (
    <div className="flex items-center gap-1 text-xs text-muted-foreground">
      <span
        className={`flex size-3 shrink-0 items-center justify-center rounded-full ${backgroundColor}`}>
        <Icon aria-hidden="true" size={iconSize} className="text-white" />
      </span>
      <span>{t(`treatment.status.${status}`)}</span>
    </div>
  )
}
